import {FC, ReactNode, useMemo} from 'react'
import {getJobStatus} from '@/shared/utils/JobHelpers'
import './style/job-details-panel.scss'

interface JobDetailsPanelProps {
  job: any
  onClose: () => void
}

function getStatusClass(status: string): string {
  const s = status.toLowerCase()
  if (s.includes('running')) return 'statusTag_RUNNING'
  if (s.includes('done') || s.includes('complete')) return 'statusTag_DONE'
  if (s.includes('cancel')) return 'statusTag_CANCELLED'
  if (s.includes('error') || s.includes('fail')) return 'statusTag_ERROR'
  if (s.includes('waiting')) return 'statusTag_WAITING'
  if (s.includes('sending')) return 'statusTag_SENDING'
  return 'statusTag_RUNNING'
}

function formatTimestamp(ts: number): string {
  return new Date(ts).toLocaleString()
}

function extractFileName(path: string): string {
  const parts = path.split('/')
  return parts[parts.length - 1] || path
}

function extractMeaningfulPath(path: string): string {
  if (path.startsWith('s3://')) {
    const withoutProtocol = path.replace(/^s3:\/\/[^/]+\//, '')
    const withoutRegionUuid = withoutProtocol.replace(/^[a-z]{2}-[a-z]+-\d+:[a-f0-9-]+\//, '')
    return withoutRegionUuid || path
  }
  return path
}

function truncatePath(path: string, maxLength = 80): string {
  const meaningful = extractMeaningfulPath(path)
  if (meaningful.length <= maxLength) return meaningful
  const start = meaningful.substring(0, 35)
  const end = meaningful.substring(meaningful.length - 40)
  return `${start}...${end}`
}

function isFileObject(value: unknown): value is {class: string; location: string} {
  return (
    value !== null &&
    typeof value === 'object' &&
    (value as any).class === 'File' &&
    !!(value as any).location
  )
}

function isSimpleArray(arr: unknown[]): arr is (string | number | boolean)[] {
  return arr.every(
    (item) => typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean'
  )
}

function isFileArray(arr: unknown[]): boolean {
  return arr.length > 0 && arr.every((item) => isFileObject(item))
}

const FileIcon: FC = () => (
  <i className='far fa-file' style={{fontSize: 'var(--ob-font-size-lg)'}} />
)

function renderFileItem(file: {location?: string}): ReactNode {
  const location = file.location || ''
  const fileName = extractFileName(location)
  const shortened = truncatePath(location)

  return (
    <div className='jdp-file-item'>
      <div className='jdp-file-icon'>
        <FileIcon />
      </div>
      <div className='jdp-file-info'>
        <div className='jdp-file-name' title={fileName}>
          {fileName}
        </div>
        <div className='jdp-file-path' title={location}>
          {shortened}
        </div>
      </div>
    </div>
  )
}

function renderSimpleChips(values: (string | number | boolean)[]): ReactNode {
  const counts = new Map<string, number>()
  values.forEach((v) => {
    const str = String(v)
    counts.set(str, (counts.get(str) || 0) + 1)
  })

  return (
    <div className='jdp-chips-container'>
      {Array.from(counts.entries()).map(([value, count]) => (
        <span className='jdp-chip' key={value}>
          {value}
          {count > 1 && <span style={{opacity: 0.6, marginLeft: 4}}>({count})</span>}
        </span>
      ))}
    </div>
  )
}

function renderValue(value: unknown): ReactNode {
  if (value === null || value === undefined) {
    return <span className='jdp-empty-value'>-</span>
  }

  if (isFileObject(value)) {
    return renderFileItem(value)
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className='jdp-empty-value'>Empty list</span>
    }

    if (isFileArray(value)) {
      return (
        <div className='jdp-file-list'>
          {value.map((file, i) => (
            <div key={i}>{renderFileItem(file as {location?: string})}</div>
          ))}
        </div>
      )
    }

    if (isSimpleArray(value)) {
      return renderSimpleChips(value)
    }

    return (
      <div className='jdp-file-list'>
        {value.map((item, i) => (
          <div className='jdp-nested-object' key={i}>
            <div className='jdp-nested-row'>
              <span className='jdp-nested-key'>[{i}]</span>
              <span className='jdp-nested-value'>{renderValue(item)}</span>
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
    if (entries.length === 0) {
      return <span className='jdp-empty-value'>Empty object</span>
    }
    return (
      <div className='jdp-nested-object'>
        {entries.map(([key, val]) => (
          <div className='jdp-nested-row' key={key}>
            <span className='jdp-nested-key'>{key}:</span>
            <span className='jdp-nested-value'>
              {typeof val === 'object' ? JSON.stringify(val) : String(val)}
            </span>
          </div>
        ))}
      </div>
    )
  }

  if (typeof value === 'boolean') {
    return (
      <span
        className='jdp-chip'
        style={{
          backgroundColor: value ? 'var(--jdp-true-bg, #e8f5e9)' : 'var(--jdp-false-bg, #ffebee)',
          color: value ? 'var(--jdp-true-color, #2e7d32)' : 'var(--jdp-false-color, #c62828)',
        }}
      >
        {value ? 'True' : 'False'}
      </span>
    )
  }

  return String(value)
}

function getParameterEntries(parameters: string | undefined): [string, unknown][] {
  if (!parameters) return []
  try {
    const parsed = JSON.parse(parameters)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return Object.entries(parsed)
    }
  } catch {
    /* malformed JSON */
  }
  return []
}

const JobDetailsPanel: FC<JobDetailsPanelProps> = ({job}) => {
  const status = useMemo(() => (job ? getJobStatus(job) : ''), [job])
  const paramEntries = useMemo(() => getParameterEntries(job?.parameters), [job?.parameters])

  if (!job) {
    return (
      <div className='job-details-panel'>
        <div className='jdp-stacked-layout'>
          <span className='jdp-empty-value'>No job details available</span>
        </div>
      </div>
    )
  }

  return (
    <div className='job-details-panel' style={{height: '100%', overflow: 'auto'}}>
      <div className='jdp-stacked-layout'>
        {/* Task Info */}
        <div className='jdp-grid-section'>
          <div className='jdp-grid-section-title'>Task Info</div>
          <table className='jdp-properties-table jdp-borderless'>
            <tbody>
              <tr>
                <td>Session ID</td>
                <td>
                  <span className='jdp-mono'>{job.SessionId}</span>
                </td>
              </tr>
              {job.serviceName && (
                <tr>
                  <td>Tool</td>
                  <td>{job.serviceName}</td>
                </tr>
              )}
              <tr>
                <td>Status</td>
                <td>
                  <span className={`status_Tag ${getStatusClass(status)}`}>{status}</span>
                </td>
              </tr>
              {job.CreationTime && (
                <tr>
                  <td>Created</td>
                  <td>{formatTimestamp(job.CreationTime)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Parameters */}
        {paramEntries.length > 0 && (
          <div className='jdp-grid-section'>
            <div className='jdp-grid-section-title'>Parameters</div>
            <table className='jdp-properties-table jdp-borderless'>
              <tbody>
                {paramEntries.map(([key, value]) => (
                  <tr key={key}>
                    <td>{key}</td>
                    <td>{renderValue(value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export {JobDetailsPanel}
